import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';

import { EClient } from '@enums/api.enum';
import { ERoutes } from '@enums/routes.enum';
import { EStyles } from '@enums/style.enum';
import useApi from '@hooks/useApi';
import useTripSocket from '@hooks/useTripSocket';
import RateTrip from '@pages/RateTrip/RateTrip';
import Search from '@pages/Search/Search';
import ThankYouFeedback from '@pages/ThankYouFeedback/ThankYouFeedback';
import TripInfo from '@pages/TripInfo/TripInfo';
import Trips from '@pages/Trips/Trips';
import { reset, setToken, setVehicleId } from '@store/auth/authSlice';
import type { RootState } from '@store/store';
import { resetTripsState } from '@store/trips/tripsSlice';
import { createSocket } from '@utils/socket';

import ProtectedRoute from './ProtectedRoute/ProtectedRoute';

const AppRoutes = () => {
	const storeToken = useSelector((state: RootState) => state.auth.token);
	const vehicleId = useSelector((state: RootState) => state.auth.vehicleId);

	const [searchParams] = useSearchParams();
	const dispatch = useDispatch();
	const { disconnectTripsSocket } = useTripSocket();
	const { getAccessToken } = useApi();
	const navigate = useNavigate();

	const getTokenByCode = async () => {
		const code = searchParams.get(EClient.code);
		const cleanCode = code?.replace(/['"]+/g, EStyles.emptyQuote);

		const vehicleId = searchParams.get(EClient.vehicleId);
		const cleanVehicleId = vehicleId?.replace(/['"]+/g, EStyles.emptyQuote);

		const token = await getAccessToken(cleanCode);

		if (token) {
			dispatch(setToken(token));
		}
		if (token || storeToken) {
			createSocket(token || (storeToken as string));
		}

		if (cleanVehicleId) {
			dispatch(resetTripsState());
			dispatch(setVehicleId(Number(cleanVehicleId)));
			navigate(ERoutes.rateTrip);
		}
	};

	useEffect(() => {
		getTokenByCode();
	}, [searchParams, dispatch, storeToken]);

	useEffect(() => {
		return () => {
			if (vehicleId) {
				dispatch(setVehicleId(undefined));
			}
			dispatch(reset());
			disconnectTripsSocket();
		};
	}, []);

	return (
		<Routes>
			<Route path={ERoutes.search} element={<Search />} />

			<Route element={<ProtectedRoute />}>
				<Route path={ERoutes.trips} element={<Trips />} />
				<Route path={`${ERoutes.tripInfo}${ERoutes.id}`} element={<TripInfo />} />
				<Route path={`${ERoutes.rateTrip}`} element={<RateTrip />} />
				<Route path={`${ERoutes.thankYou}`} element={<ThankYouFeedback />} />
			</Route>

			<Route path={ERoutes.any} element={<Navigate to={ERoutes.search} replace />} />
		</Routes>
	);
};

export default AppRoutes;
